<template>
  <div class="mapping-load-modal-wrapper">
    <dcModal
      :modalShown="show"
      @close-modal="close"
      modalCenter
      :modalClasses="['mapping-load-modal']"
    >
      <template #header>
        <h3 class="modal-title">Load Saved Mapping</h3>
        <button class="close-modal btn-close" aria-label="Close">&times;</button>
      </template>

      <template #body>
        <div v-if="loading" class="loading-indicator">
          <div class="spinner"></div>
          <div class="loading-text">Loading saved mappings...</div>
        </div>

        <div v-else-if="!savedMappings.length" class="empty-state">
          <div class="empty-icon">
            <i class="fa fa-folder-open-o" aria-hidden="true"></i>
          </div>
          <div class="empty-text">No saved mappings found</div>
        </div>

        <ul v-else class="mapping-list" role="list">
          <li
            v-for="mapping in savedMappings"
            :key="mapping.id"
            class="mapping-item"
            @click="selectMapping(mapping)"
            role="button"
            tabindex="0"
            @keyup.enter="selectMapping(mapping)"
            @keyup.space.prevent="selectMapping(mapping)"
          >
            <div class="mapping-content">
              <div class="mapping-title">{{ mapping.name }}</div>
              <div class="mapping-subtitle">
                Created: {{ formatDate(mapping.created_at) }}
              </div>
            </div>

            <button
              class="btn-icon btn-delete"
              @click.stop="promptDelete(mapping)"
              @keyup.enter.stop="promptDelete(mapping)"
              title="Delete mapping"
              aria-label="Delete mapping"
            >
              <i class="fa fa-trash" aria-hidden="true"></i>
            </button>
          </li>
        </ul>
      </template>

      <template #footer>
        <button class="btn btn-secondary" @click="close">
          Cancel
        </button>
      </template>
    </dcModal>

    <!-- Delete confirmation modal -->
    <dcModal
      v-if="deleteConfirm"
      :modalShown="true"
      @close-modal="cancelDelete"
      modalCenter
      :modalClasses="['delete-confirm-modal']"
    >
      <template #header>
        <h3 class="modal-title">Delete Mapping</h3>
        <button class="close-modal btn-close" aria-label="Close">&times;</button>
      </template>

      <template #body>
        <p>Are you sure you want to delete "<strong>{{ deleteConfirm.name }}</strong>"?</p>
        <p class="text-muted">This cannot be undone.</p>
      </template>

      <template #footer>
        <button class="btn btn-secondary" @click="cancelDelete" :disabled="deleting">
          Cancel
        </button>
        <button class="btn btn-danger" :disabled="deleting" @click="confirmDelete">
          <span v-if="deleting" class="spinner-small" aria-hidden="true"></span>
          {{ deleting ? 'Deleting...' : 'Delete' }}
        </button>
      </template>
    </dcModal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { deleteStudentImportMapping } from '../../../../DriveScout/API/models/ImportWizard';
import dcModal from '../../../common/directives/vue-directives/modal/Modal.vue';

export default {
  components: {
    dcModal
  },

  props: {
    show: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      loading: true,
      savedMappings: [],
      deleteConfirm: null,
      deleting: false
    }
  },

  computed: {
    ...mapGetters(['schoolId'])
  },

  created() {
    this.loadSavedMappings();
  },

  methods: {
    loadSavedMappings() {
      this.loading = true;

      this.$store.dispatch('GET_STUDENT_IMPORT_MAPPINGS', { school_id: this.schoolId })
        .then(response => {
          this.savedMappings = response.data;
          this.loading = false;
        })
        .catch(error => {
          this.$store.dispatch('ui/ADD_FLASH_MESSAGE', {
            type: 'error',
            message: 'Failed to load saved mappings: ' + (error.response?.data?.detail || error.response?.data?.error || 'Unknown error')
          });
          this.loading = false;
        });
    },

    selectMapping(mapping) {
      this.$store.dispatch('GET_STUDENT_IMPORT_MAPPING', { school_id: this.schoolId, mapping_id: mapping.id })
        .then(response => {
          this.$emit('load', response.data);
        })
        .catch(error => {
          this.$store.dispatch('ui/ADD_FLASH_MESSAGE', {
            type: 'error',
            message: 'Failed to load mapping: ' + (error.response?.data?.detail || error.response?.data?.error || 'Unknown error')
          });
        });
    },

    promptDelete(mapping) {
      this.deleteConfirm = mapping;
    },

    cancelDelete() {
      this.deleteConfirm = null;
    },

    async confirmDelete() {
      this.deleting = true;
      try {
        await deleteStudentImportMapping(this.schoolId, this.deleteConfirm.id);
        this.savedMappings = this.savedMappings.filter(m => m.id !== this.deleteConfirm.id);
        this.$store.dispatch('ui/ADD_FLASH_MESSAGE', {
          type: 'success',
          message: 'Mapping deleted successfully'
        });
        this.deleteConfirm = null;
      } catch (error) {
        this.$store.dispatch('ui/ADD_FLASH_MESSAGE', {
          type: 'error',
          message: 'Failed to delete mapping: ' + (error.response?.data?.detail || error.response?.data?.error || 'Unknown error')
        });
      } finally {
        this.deleting = false;
      }
    },

    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    },

    close() {
      this.$emit('close');
    }
  }
}
</script>

<style scoped lang="scss">
@import '../../../../assets/css/partials/variables';

.modal-title {
  margin: 0;
  font-size: 18px;
  font-weight: 500;
}

.btn-close {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #757575;
  padding: 0;
  line-height: 1;
}

.btn-close:hover {
  color: #333;
}

.loading-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid $green;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loading-text {
  margin-top: 16px;
  color: #757575;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
}

.empty-icon {
  font-size: 48px;
  color: #757575;
  margin-bottom: 16px;
}

.empty-text {
  color: #757575;
}

.mapping-list {
  list-style: none;
  padding: 0;
  margin: 0;
  max-height: 300px;
  overflow-y: auto;
}

.mapping-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #e0e0e0;
  cursor: pointer;
  transition: background-color 0.2s;
}

.mapping-item:last-child {
  border-bottom: none;
}

.mapping-item:hover {
  background-color: #f5f5f5;
}

.mapping-item:focus {
  outline: none;
  background-color: rgba($green, 0.1);
}

.mapping-content {
  flex: 1;
}

.mapping-title {
  font-weight: 500;
  margin-bottom: 4px;
}

.mapping-subtitle {
  font-size: 12px;
  color: #757575;
}

.btn-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background-color: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
}

.btn-icon:hover {
  background-color: rgba(0, 0, 0, 0.1);
}

.btn-delete {
  color: $red;
}

.btn-delete:hover {
  background-color: rgba($red, 0.1);
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.3s;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-secondary {
  background-color: #f5f5f5;
  color: #757575;
}

.btn-secondary:hover:not(:disabled) {
  background-color: #e0e0e0;
}

.btn-danger {
  background-color: $red;
  color: white;
}

.btn-danger:hover:not(:disabled) {
  background-color: $dark-red;
}

.text-muted {
  color: #757575;
  font-size: 14px;
}

.spinner-small {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>

<style lang="scss">
/* Modal container sizing - not scoped */
.mapping-load-modal {
  width: 600px;
  max-width: 90%;
}

.mapping-load-modal .modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #e0e0e0;
}

.mapping-load-modal .modal-body {
  padding: 0;
  min-height: 200px;
}

.mapping-load-modal .modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 16px;
  border-top: 1px solid #e0e0e0;
}

.delete-confirm-modal {
  width: 400px;
  max-width: 90%;
}

.delete-confirm-modal .modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #e0e0e0;
}

.delete-confirm-modal .modal-body {
  padding: 16px;
}

.delete-confirm-modal .modal-body p {
  margin: 0 0 8px 0;
}

.delete-confirm-modal .modal-body p:last-child {
  margin-bottom: 0;
}

.delete-confirm-modal .modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 16px;
  border-top: 1px solid #e0e0e0;
}
</style>
